import { makeAutoObservable, runInAction } from "mobx";
import { ACTIONS, CallBackProps, ORIGIN, STATUS, Step } from "react-joyride";
import { IRootStore } from "../mobx/root-store";
import { TourConfig, TourStatus, TourStep } from "./types";

export interface ITourStore {
	activeTour: TourConfig | null;
	status: TourStatus;
	stepIndex: number;
	run: boolean;
	refreshKey: number;

	readonly currentStep: TourStep | null;
	readonly joyrideSteps: Step[];

	start(tour: TourConfig): void;
	stop(): void;
	pause(): void;
	resume(): void;
	handleJoyrideCallback(data: CallBackProps): void;
}

export default class TourStore implements ITourStore {
	private rootStore: IRootStore;

	activeTour: TourConfig | null = null;
	status: TourStatus = "idle";
	stepIndex: number = 0;
	run: boolean = false;
	refreshKey: number = 0;

	constructor(rootStore: IRootStore) {
		this.rootStore = rootStore;
		makeAutoObservable(this, {
			// Exclude from observation: contains DOM elements and React components that shouldn't be proxied
			joyrideSteps: false,
			// Exclude activeTour: it contains targetRef objects that must not be proxied by MobX.
			// MobX deep-proxies objects, which snapshots ref.current values at observation time.
			// This breaks ref updates made externally (e.g., in PlaygroundImages.tsx).
			activeTour: false,
		});
	}

	get currentStep(): TourStep | null {
		if (!this.activeTour) return null;
		return this.activeTour.steps[this.stepIndex] ?? null;
	}

	get joyrideSteps(): Step[] {
		const tour = this.activeTour;
		if (!tour) return [];

		return tour.steps.map((step) => ({
			...step,
			target: step.targetRef.current || "body",
		}));
	}

	/**
	 * Start a tour with the given configuration.
	 * @param tour - The tour configuration containing steps and callbacks
	 */
	start = (tour: TourConfig): void => {
		runInAction(() => {
			this.activeTour = tour;
			this.stepIndex = 0;
			this.status = "running";
			this.run = true;
			this.refreshKey++;
		});

		// Check if first step's target is available
		this.ensureTargetAvailable();
	};

	stop = (): void => {
		runInAction(() => {
			this.activeTour = null;
			this.status = "idle";
			this.stepIndex = 0;
			this.run = false;
		});
	};

	pause = (): void => {
		runInAction(() => {
			this.status = "paused";
			this.run = false;
		});
	};

	/**
	 * Resume the tour. Waits one frame for React to finish rendering and setting refs,
	 * then resumes with refreshed step targets.
	 */
	resume = (): void => {
		runInAction(() => {
			this.status = "running";
			this.run = true;
			this.refreshKey++;
		});
	};

	handleJoyrideCallback = async (data: CallBackProps): Promise<void> => {
		const { action, status, index, type } = data;

		// Handle close button click
		if (action === ACTIONS.CLOSE && data.origin === ORIGIN.BUTTON_CLOSE) {
			await this.handleSkip();
			return;
		}

		// Handle tour completion
		if (status === STATUS.FINISHED) {
			return await this.handleComplete();
		}

		// Handle tour skip (ESC key, overlay click, etc.)
		if (status === STATUS.SKIPPED) {
			return await this.handleSkip();
		}

		// Handle step change forward
		if (type === "step:after" && action === ACTIONS.NEXT) {
			const nextIndex = index + 1;
			const nextStep = this.activeTour?.steps[nextIndex];

			if (nextStep) {
				runInAction(() => {
					this.stepIndex = nextIndex;
				});

				// Wait for target to be available before showing next step
				this.ensureTargetAvailable();
			} else {
				// No more steps - complete the tour
				await this.handleComplete();
			}
		}

		// Handle step change backwards
		if (type === "step:after" && action === ACTIONS.PREV) {
			runInAction(() => {
				this.stepIndex = Math.max(0, index - 1);
				this.refreshKey++;
			});
		}
	};

	/**
	 * Ensures the current step's target element is available.
	 * If not, pauses the tour until resume() is called externally.
	 */
	private ensureTargetAvailable = (): void => {
		const step = this.currentStep;
		if (!step?.targetRef.current) {
			this.pause();
		}
	};

	private handleComplete = async (): Promise<void> => {
		const tour = this.activeTour;

		if (tour?.onComplete) {
			await tour.onComplete(this.rootStore);
		}

		runInAction(() => {
			this.status = "completed";
		});

		this.stop();
	};

	private handleSkip = async (): Promise<void> => {
		const tour = this.activeTour;
		if (tour?.onSkip) {
			await tour.onSkip(this.rootStore);
		} else if (tour?.onComplete) {
			// Fall back to onComplete if onSkip is not defined
			await tour.onComplete(this.rootStore);
		}

		this.stop();
	};
}
